import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Folder, Image } from 'lucide-react';
import apiClient from '../lib/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import UserProfileMenu from '../components/UserProfileMenu';

const Projects = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data } = await apiClient.get('/api/projects');
      setProjects(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await apiClient.post('/api/projects', { title, description });
      setShowCreateModal(false);
      setTitle('');
      setDescription('');
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      alert(copy.projects.createFailed);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D]">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={copy.media.logo} alt={copy.common.logoAlt} className="h-11 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-[#B6B3C9] hover:text-[#F5F4FF] flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span>{copy.projects.backToDashboard}</span>
            </button>
            <UserProfileMenu user={user} copy={copy} onLogout={handleLogout} />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-light tracking-tight text-[#F5F4FF]">{copy.projects.title}</h1>
          <button
            data-testid="create-project-button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-6 py-3 transition-all duration-300 shadow-[0_4px_14px_rgba(124,92,255,0.32)] font-medium"
          >
            <Plus className="w-5 h-5" strokeWidth={2} />
            <span>{copy.projects.newProject}</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7C5CFF] mx-auto mb-4"></div>
            <p className="text-[#B6B3C9]">{copy.projects.loading}</p>
          </div>
        ) : projects.length === 0 ? (
          <div data-testid="empty-projects" className="bg-[#141417] border border-[#2B2B35]/50 rounded-2xl p-12 text-center">
            <div className="bg-[#1C1C21] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Folder className="w-10 h-10 text-[#8F8AA8]" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-medium text-[#F5F4FF] mb-2">{copy.projects.emptyTitle}</h3>
            <p className="text-[#B6B3C9] mb-6">{copy.projects.emptyHelp}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center space-x-2 bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-8 py-3 transition-all font-medium"
            >
              <Plus className="w-5 h-5" strokeWidth={2} />
              <span>{copy.projects.createFirst}</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-${project.id}`}
                className="bg-[#141417] border border-[#2B2B35]/50 rounded-2xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-[#1C1C21] p-3 rounded-xl">
                    <Folder className="w-6 h-6 text-[#7C5CFF]" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-[#F5F4FF] mb-2">{project.title}</h3>
                {project.description && (
                  <p className="text-sm text-[#B6B3C9] mb-4 line-clamp-2">{project.description}</p>
                )}
                <div className="text-xs text-[#8F8AA8]">
                  {copy.projects.created} {new Date(project.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#141417] rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-light text-[#F5F4FF] mb-6">{copy.projects.modalTitle}</h2>
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.projects.projectTitle}</label>
                <input
                  data-testid="project-title-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8]"
                  placeholder={copy.projects.projectTitlePlaceholder}
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#F5F4FF] mb-2">{copy.projects.projectDescription}</label>
                <textarea
                  data-testid="project-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0B0B0D] border border-[#2B2B35] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C5CFF]/20 focus:border-[#7C5CFF] transition-all text-[#F5F4FF] placeholder-[#8F8AA8] resize-none"
                  placeholder={copy.projects.projectDescriptionPlaceholder}
                  rows={3}
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-transparent border border-[#2B2B35] text-[#F5F4FF] hover:border-[#F5F4FF] rounded-full px-6 py-3 transition-all font-medium"
                >
                  {copy.projects.cancel}
                </button>
                <button
                  data-testid="create-project-submit"
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-[#7C5CFF] text-white hover:bg-[#6B4DFF] rounded-full px-6 py-3 transition-all font-medium disabled:opacity-50"
                >
                  {creating ? copy.projects.creating : copy.projects.create}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;


